import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Navigate, useMatch, useNavigate } from "react-router-dom";
import { ROLE_ADMIN, ROLE_MENTOR, ROLE_STUDENT, ROLE_SUPER_ADMIN, SUPER_PERMISSIONS } from "../models/Constants";
import { findNumbersOnArray, isEmpty } from "../helpers/Functions";
import { getLibraryAccess } from "./crud/library.crud";
import displayToast from "../theme/partials/DisplayToast";
import { setCurrentCapabilities, setUserCapabilities, updateCapabilitiesAsync } from "./redux/slices/authSlice";
import { getCompanyCapabilities } from "../helpers/CompanyUtils";
import { isNotStudent } from "../helpers/RoleUtils";
import { useDispatch } from "react-redux";
import { usePermissionProviderContext, PermissionProviderContext } from "../contexts/contexts";
import Loading from "../theme/partials/Loading";
import { useSelector } from "react-redux";

const YesNoDialog = lazy(() => import("../theme/partials/YesNoDialog"));
const OKDialog = lazy(() => import("../theme/partials/OKDialog"));

export const LibraryPageRestriction = ({ children }) => {
	const [showConfirmCompanySwitchDialog, setShowConfirmCompanySwitchDialog] = useState(false);
	const [showStudentNoAccessDialog, setShowStudentNoAccessDialog] = useState(false);

	const toSwitchCapability = useRef();

	const { currentCompanyId, currentStandardRoleId, setCurrentCompany, setCurrentCapabilitiesByRole } =
		usePermissionProviderContext();

	const libraryPageLocation = useMatch("/dashboard/:type/:library_code");
	const navigate = useNavigate();

	let libraryCode = libraryPageLocation?.params?.library_code;

	const confirmCompanySwitch = () => {
		const capability = toSwitchCapability.current;

		if (!isEmpty(capability?.company_id) && currentCompanyId !== capability?.company_id) {
			setCurrentCompany(capability?.company_id, false);

			return;
		}

		if (!isEmpty(capability?.role_standard_id) && currentStandardRoleId !== capability?.role_standard_id) {
			setCurrentCapabilitiesByRole(capability?.role_standard_id, false);
		}
	};

	const userDetails = useSelector((state) => state.auth.user_details);

	useEffect(() => {
		if (isEmpty(libraryCode) || isEmpty(currentStandardRoleId)) {
			return;
		}

		getLibraryAccess(libraryCode).then((response) => {
			if (response.data.init[0].status == "error") {
				displayToast("error", response.data.init[0]["message"]);
				return;
			} else {
				const access = response.data?.data ?? [];

				let newValidRoleStandardId = null;
				let newAccessCompanyId = null;

				if(access?.[0]?.created_by === userDetails?.id){
					return;
				}

				for (let accessRole of access) {
					let roleStandardId = accessRole.standard_id;
					let accessRoleEnrollmentDetails = accessRole?.enrollment_details;
					let roleName = accessRole?.role_name?.toLowerCase();

					if (roleName === "student" || isEmpty(roleName)) {
						roleStandardId = ROLE_STUDENT;
					}

					newAccessCompanyId = accessRole.company_id;

					if (!isEmpty(accessRoleEnrollmentDetails) && roleStandardId === ROLE_STUDENT) {
						let hasValidAccess =
							isEmpty(accessRoleEnrollmentDetails?.remaining_days) ||
							accessRoleEnrollmentDetails?.remaining_days > 0;
						hasValidAccess =
							(hasValidAccess || !isEmpty(accessRoleEnrollmentDetails?.completed_at)) &&
							Boolean(accessRoleEnrollmentDetails?.has_access);

						if (hasValidAccess) {
							newValidRoleStandardId = ROLE_STUDENT;
						}
					}

					if (roleStandardId === ROLE_MENTOR && accessRole?.has_mentor_library) {
						newValidRoleStandardId = ROLE_MENTOR;
					}

					if (roleStandardId === ROLE_ADMIN) {
						newValidRoleStandardId = ROLE_ADMIN;
					}

					if (newValidRoleStandardId === currentStandardRoleId) {
						break; // stop here
					}
				}

				if (isEmpty(newValidRoleStandardId) || isEmpty(newAccessCompanyId)) {
					setShowStudentNoAccessDialog(true);
					return;
				}

				if (newValidRoleStandardId === ROLE_STUDENT) {
					return;
				}

				if (currentCompanyId == newAccessCompanyId && currentStandardRoleId == newValidRoleStandardId) {
					return;
				}

				toSwitchCapability.current = {
					company_id: newAccessCompanyId,
					role_standard_id: newValidRoleStandardId,
				};

				setShowConfirmCompanySwitchDialog(true);
			}
		});
	}, [libraryCode, currentStandardRoleId]);

	return (
		<Suspense fallback={<Loading />}>
			<YesNoDialog
				message={`Accessing this course will switch you to a different organization. Do you want to continue?`}
				open={showConfirmCompanySwitchDialog}
				confirm={confirmCompanySwitch}
				onNo={() => {
					navigate("/", { replace: true });
				}}
				onClose={() => {
					setShowConfirmCompanySwitchDialog(false);
				}}
			/>
			<OKDialog
				message={`You currently do not have access to this course. This could be because your enrollment has expired, access has been revoked, or you are not enrolled yet.`}
				open={showStudentNoAccessDialog}
				confirm={() => {
					navigate("/", { replace: true });
				}}
			/>
			{children}
		</Suspense>
	);
};

export const PermissionWrapper = ({ children, requiredPermissions = [], requiredRoles = [], redirectTo = "/" }) => {
	const { currentStandardRoleId } = usePermissionProviderContext();

	const requiredPermissionMap = useMemo(() => {
		return requiredPermissions.reduce((map, permission, index) => {
			map[permission] = index;

			return map;
		}, {});
	}, [requiredPermissions]);

	const requiredRolesMap = useMemo(() => {
		return requiredRoles.reduce((map, role, index) => {
			map[role] = index;

			return map;
		}, {});
	}, [requiredRoles]);

	const userHasPermission = (permission) => {
		return !isEmpty(requiredPermissionMap[permission] ?? null);
	};

	const userHasRequiredRole = () => {
		return !isEmpty(requiredRolesMap[currentStandardRoleId] ?? null);
	};

	return <Navigate to={redirectTo} replace />;
};

const PermissionProvider = ({
	isSuperAdmin,
	currentCapabilities,
	userCapabilities,
	setUserCapabilities,
	children,
	setCurrentCapabilities,
}) => {
	const [currentCompanyId, setCurrentCompanyId] = useState(null);
	const [userAllowedModels, setUserAllowedModels] = useState([]);
	const [currentActiveModel, setCurrentActiveModel] = useState({});
	const [isOnAgentMode, setIsOnAgentMode] = useState(false);

	const queryParams = new URLSearchParams(window.location.search);
	const learningHabitEmailID = +queryParams.get("lh");
	const learningHabitLessonID = +queryParams.get("lh_email");
	const learningHabitCategoryID = +queryParams.get("lh_category");
	const learningHabitLibID = +queryParams.get("lh_lib");
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const currentCompany = currentCapabilities?.company ?? {};
	const currentRoles = currentCompany?.roles ?? [];
	const hasChatAI = Boolean(currentCompany?.settings?.has_chat_ai);

	const userRolesMap = useMemo(() => {
		return currentCompany?.roles?.reduce((map, role) => {
			let roleId = role?.role_standard_id;

			if (isEmpty(roleId) && role?.role_name?.toLowerCase() === "student") {
				roleId = ROLE_STUDENT;
			}

			if (!isEmpty(roleId)) {
				map[roleId] = role;
			}

			return map;
		}, {});
	}, [currentCapabilities]);

	let userRoles = currentCompany?.roles ?? [];
	let currentStandardRoleId = currentCapabilities?.role_standard_id;
	const currentPermissions = currentCapabilities?.role == ROLE_SUPER_ADMIN ? SUPER_PERMISSIONS :
		currentCapabilities.company?.roles.find(role => role.role_id == currentCapabilities.role)?.permissions ?? [];
	const isPrioCompany = process.env.REACT_APP_IN_COMPANY_ID?.split(",").includes(String(currentCompanyId));

	const hasPermissions = useCallback((requiredPermissions = [], doPrioSuperAdmin = true) => {
		if (doPrioSuperAdmin && isSuperAdmin) {
			return true;
		}

		return findNumbersOnArray(currentPermissions, requiredPermissions);
	}, [currentPermissions, isSuperAdmin]);

	const companyHasFeatures = useCallback((features = [], doPrioSuperAdmin = false) => {
		if (currentStandardRoleId === ROLE_SUPER_ADMIN && doPrioSuperAdmin) {
			return true;
		}

		const companySettings = currentCompany?.settings ?? {};

		if (Array.isArray(features)) {
			return features.some((feature) => {
				return Boolean(companySettings[feature]);
			});
		}

		return Boolean(companySettings[features]);
	}, [currentCompany?.settings], currentStandardRoleId);

	useEffect(() => {
		setCurrentCompanyId(currentCompany?.company_id);
	}, [currentCompany?.company_id, currentStandardRoleId]);

	const updateUserCapabilities = useCallback((companyId, updates = {}) => {
		let updatedUserCapabilities = userCapabilities.map((capability) => {
			if (capability.company_id === companyId) {
				return {
					...capability,
					...updates,
					settings: {
						...capability?.settings,
						...updates?.settings,
					},
				};
			}
			return capability;
		});

		setUserCapabilities(updatedUserCapabilities);
	}, [userCapabilities, setUserCapabilities]);

	useEffect(() => {
		if (!isEmpty(currentCapabilities)) {
			setCurrentCompanyId(currentCapabilities?.company?.company_id);
		}
	}, []);

	const setCurrentCapabilitiesByRole = (roleId, redirect = true, filterByStandardId = false) => {
		const selectedRole = userRoles?.find((role) => (filterByStandardId ? role?.role_standard_id : role?.role_id) === roleId);

		let newCurrentCapabilities = {
			...currentCapabilities,
			role: selectedRole?.role_id,
			role_name: selectedRole?.role_name,
			role_standard_id: roleId,
			is_limited: selectedRole?.is_limited,
		};

		if (roleId === ROLE_SUPER_ADMIN) {
			newCurrentCapabilities.role = ROLE_SUPER_ADMIN;
			newCurrentCapabilities.role_name = "Otoma Admin";
			newCurrentCapabilities.role_standard_id = ROLE_SUPER_ADMIN;
		}

		if (!isEmpty(selectedRole) || roleId === ROLE_SUPER_ADMIN) {
			setCurrentCapabilities(newCurrentCapabilities);
		}

		if (roleId === ROLE_SUPER_ADMIN) {
			navigate("/admin-panel");
		} else if (redirect) {
			// if the url came from a learning habit email, set the access to student and redirect back
			if (
				isNotStudent(currentCapabilities.role) &&
				learningHabitEmailID &&
				learningHabitCategoryID &&
				learningHabitLibID &&
				learningHabitLessonID
			) {
				return;
			} else {
				navigate("/");
			}
		}
	};

	const setCurrentCompany = (companyId, redirect = true, cb = () => { }) => {
		let selected = getCompanyCapabilities(userCapabilities, companyId);

		if (isEmpty(selected)) {
			return;
		}

		let updatedCapabilities = { ...currentCapabilities };
		updatedCapabilities.company = selected;

		// Use the Mentor or Admin role if the user qualifies for either.
		let roleUpdate = selected?.roles.find(
			(role) =>
				role.role_standard_id === ROLE_ADMIN ||
				role.role_id === ROLE_ADMIN ||
				role.role_standard_id === ROLE_MENTOR ||
				role.role_id === ROLE_MENTOR
		);

		// If the user is not admin or mentor select the first role
		if (isEmpty(roleUpdate)) {
			roleUpdate = selected?.roles[0];
		}

		updatedCapabilities.role = roleUpdate?.role_id;
		updatedCapabilities.role_name = roleUpdate?.role_name;
		updatedCapabilities.is_limited = roleUpdate?.is_limited;
		updatedCapabilities.role_standard_id = roleUpdate?.role_standard_id;

		dispatch(updateCapabilitiesAsync(updatedCapabilities))
			.then(() => {
				cb(companyId);

				if (redirect) {
					window.location.href = "/";
				}
			})
			.catch((error) => {
				console.error("Error updating capabilities", error);
			});
	};

	return (
		<PermissionProviderContext.Provider
			value={{
				isPrioCompany,
				hasChatAI,
				currentCompanyId,
				currentStandardRoleId,
				currentPermissions,
				isAdmin: currentStandardRoleId === ROLE_ADMIN,
				currentRoles,
				hasPermissions,
				companyHasFeatures,
				setCurrentCompany,
				updateUserCapabilities,
				setCurrentCapabilitiesByRole,
				setCurrentCompanyId,
				userAllowedModels,
				setUserAllowedModels,
				currentActiveModel,
				setCurrentActiveModel,
				isOnAgentMode, 
				setIsOnAgentMode,
			}}
		>
			{children}
		</PermissionProviderContext.Provider>
	);
};

const mapStateToProps = (state) => {
	let data = {
		currentCapabilities: state.auth.current_capabilities,
		userCapabilities: state.auth.user_capabilities,
		isSuperAdmin: state.auth.is_super_admin,
	};

	return data;
};

const mapDispatchToProps = {
	setUserCapabilities,
	setCurrentCapabilities,
};

export default connect(mapStateToProps, mapDispatchToProps)(PermissionProvider);
